
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

interface ProgressData {
  year: string;
  target: number;
  realisasi: number;
  percentage: number;
  activities: string[];
  challenges: string[];
  achievements: string[];
}

export default function MonevPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'progress' | 'impact' | 'methodology'>('overview');

  const progressData: ProgressData[] = [
    {
      year: '2021',
      target: 120,
      realisasi: 98,
      percentage: 82,
      activities: ['Baseline monitoring system establishment', 'Data collection protocol development', 'Staff training and capacity building'],
      challenges: ['Limited technical infrastructure', 'Data standardization issues', 'Remote site accessibility'],
      achievements: ['98 monitoring sites established', '15,680 data points collected', 'Formation of 12 monitoring teams']
    },
    {
      year: '2022',
      target: 180,
      realisasi: 167,
      percentage: 93,
      activities: ['Digital monitoring platform deployment', 'Real-time data collection systems', 'Automated alert mechanisms'],
      challenges: ['Technology adoption barriers', 'Internet connectivity issues', 'Equipment maintenance challenges'],
      achievements: ['167 evaluation reports completed', '28,450 data points processed', 'Established 18 field stations']
    },
    {
      year: '2023',
      target: 240,
      realisasi: 228,
      percentage: 95,
      activities: ['Advanced analytics implementation', 'Predictive modeling systems', 'Stakeholder dashboard development'],
      challenges: ['Data quality assurance', 'System integration complexity', 'User training requirements'],
      achievements: ['228 comprehensive assessments', '41,230 measurements recorded', '25 automated monitoring stations']
    },
    {
      year: '2024',
      target: 300,
      realisasi: 289,
      percentage: 96,
      activities: ['AI-powered analysis tools', 'Blockchain data verification', 'Mobile monitoring applications'],
      challenges: ['Scaling data processing capacity', 'Ensuring data security', 'Maintaining system reliability'],
      achievements: ['289 detailed evaluations', '52,890 verified data points', '35 smart monitoring nodes']
    }
  ];

  const impactMetrics = [
    { label: 'Monitoring Sites Active', value: '782', unit: 'Locations', icon: 'ri-map-pin-line', color: '#2D8B57' },
    { label: 'Data Points Collected', value: '138,250', unit: 'Measurements', icon: 'ri-database-line', color: '#20B2AA' },
    { label: 'Evaluation Reports', value: '782', unit: 'Reports', icon: 'ri-file-chart-line', color: '#FF6B35' },
    { label: 'System Reliability', value: '94%', unit: 'Uptime', icon: 'ri-shield-check-line', color: '#32CD32' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-[#2D8B57] to-[#20B2AA] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-line-chart-line text-3xl"></i>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Monitoring & Evaluasi Program
              </h1>
              <p className="text-xl sm:text-2xl mb-8 opacity-90">
                Comprehensive monitoring and evaluation system ensuring program effectiveness and accountability
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#2D8B57] whitespace-nowrap"
                >
                  <i className="ri-dashboard-line mr-2"></i>
                  Live Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#2D8B57] whitespace-nowrap"
                >
                  <i className="ri-file-chart-line mr-2"></i>
                  Download Reports
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  { id: 'overview', label: 'Program Overview', icon: 'ri-eye-line' },
                  { id: 'progress', label: 'Progress & Targets', icon: 'ri-bar-chart-line' },
                  { id: 'impact', label: 'Impact Assessment', icon: 'ri-award-line' },
                  { id: 'methodology', label: 'Methodology', icon: 'ri-file-text-line' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[#2D8B57] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${tab.icon} mr-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Monitoring & Evaluasi Program Overview
                  </h2>
                  <p className="text-lg text-gray-600">
                    Data-driven system ensuring program effectiveness and continuous improvement
                  </p>
                </div>

                {/* Storytelling Section */}
                <div className="mb-16">
                  <div className="bg-gradient-to-r from-[#2D8B57]/10 to-[#20B2AA]/10 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-database-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-[#333333]">Data That Drives Change</h3>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        "Every number tells a story," says Dr. Andi Wijaya, M4CR's Monitoring & Evaluation Director. In the control room of M4CR's Jakarta headquarters, dozens of screens display real-time data from 782 monitoring sites across Indonesia. When mangrove survival rates at Site 47 dropped below 80%, the system automatically flagged the issue within 24 hours, triggering rapid intervention that saved the entire restoration area.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div>
                          <img
                            src="https://public.readdy.ai/ai/img_res/26c812d0533e59c5b4c269b1b77e28e7.jpg"
                            alt="Field Monitoring Operations"
                            className="w-full h-48 object-cover rounded-lg mb-4"
                          />
                          <p className="text-sm text-gray-600 italic">
                            Dr. Andi Wijaya reviewing real-time monitoring data at M4CR's command center, Jakarta
                          </p>
                        </div>
                        
                        <div className="flex flex-col justify-center">
                          <blockquote className="border-l-4 border-[#2D8B57] pl-6 mb-6">
                            <p className="text-lg text-gray-700 italic mb-4">
                              "We don't just collect data—we turn it into actionable insights. Our monitoring system caught problems that would have taken months to discover through traditional methods."
                            </p>
                            <footer className="text-[#2D8B57] font-bold">— Dr. Andi Wijaya, M&E Director</footer>
                          </blockquote>
                          
                          <p className="text-gray-700 leading-relaxed">
                            Dr. Andi's approach represents M4CR's commitment to evidence-based conservation—using cutting-edge technology and rigorous analysis to ensure every program delivers measurable impact.
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-[#333333] mb-3">The Science of Accountability: 94% System Reliability</h5>
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          M4CR's monitoring system processes over 138,000 data points from 782 active sites, using IoT sensors, mobile apps, and satellite imagery to track program performance in real-time. This comprehensive approach achieves 94% system reliability and enables rapid response to emerging challenges.
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-[#2D8B57] mb-1">782</div>
                            <div className="text-xs text-gray-600">Active sites</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#20B2AA] mb-1">138,250</div>
                            <div className="text-xs text-gray-600">Data points</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#32CD32] mb-1">94%</div>
                            <div className="text-xs text-gray-600">System reliability</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Journalist Report Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-newspaper-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#333333]">Field Report: The Science of Accountability</h3>
                        <p className="text-gray-600">How M4CR maintains 94% program compliance through advanced monitoring</p>
                      </div>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <div className="bg-[#F8F9FA] border-l-4 border-[#20B2AA] p-6 mb-6 rounded-r-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Technical Investigation</strong> • December 2024 • M4CR Research Team
                        </p>
                        <h4 className="text-lg font-bold text-[#333333] mb-3">
                          Inside the Numbers: How Technology Transforms Conservation Accountability
                        </h4>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed mb-6">
                        <strong>JAKARTA, Indonesia</strong> — In a sector where many conservation programs struggle with accountability, M4CR's monitoring and evaluation system stands out with 94% compliance rates across 782 active sites. Our investigation reveals the systematic approach behind this exceptional performance, combining cutting-edge technology with rigorous scientific methods.
                      </p>
                      
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-blue-800 mb-4">The Three-Tier Monitoring System</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Community Level (Daily)</h6>
                            <ul className="space-y-2 text-sm text-blue-700">
                              <li>• Local observer reports</li>
                              <li>• Mobile app data collection</li>
                              <li>• Photo documentation</li>
                              <li>• GPS-tagged measurements</li>
                            </ul>
                          </div>
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Field Teams (Monthly)</h6>
                            <ul className="space-y-2 text-sm text-blue-700">
                              <li>• Professional assessments</li>
                              <li>• Equipment calibration</li>
                              <li>• Quality control checks</li>
                              <li>• Data verification</li>
                            </ul>
                          </div>
                          <div>
                            <h6 className="font-semibold text-blue-700 mb-3">Independent Audits (Quarterly)</h6>
                            <ul className="space-y-2 text-sm text-blue-700">
                              <li>• Third-party verification</li>
                              <li>• Satellite imagery analysis</li>
                              <li>• Statistical validation</li>
                              <li>• Compliance reporting</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <blockquote className="border-l-4 border-[#2D8B57] pl-6 mb-6 bg-[#F8F9FA] p-4 rounded-r-lg">
                        <p className="text-lg text-gray-700 italic mb-4">
                          "We've eliminated the black box problem in conservation. Every data point is GPS-tagged, time-stamped, and photo-verified. Our mobile app makes it impossible to fabricate data."
                        </p>
                        <footer className="text-[#2D8B57] font-bold">— Dr. Andi Wijaya, M&E Director</footer>
                      </blockquote>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Program Description */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Program Objectives</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#2D8B57]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Real-time Monitoring</h4>
                          <p className="text-gray-600 text-sm">
                            Continuous data collection and analysis across all program activities and sites
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#20B2AA]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Impact Evaluation</h4>
                          <p className="text-gray-600 text-sm">
                            Systematic assessment of program outcomes and long-term effectiveness
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#FF6B35]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#FF6B35]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Adaptive Management</h4>
                          <p className="text-gray-600 text-sm">
                            Evidence-based program improvements and strategic adjustments
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#32CD32]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#32CD32]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Transparency & Accountability</h4>
                          <p className="text-gray-600 text-sm">
                            Open data sharing and stakeholder reporting for program accountability
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">System Components</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Data Collection Network</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          IoT sensors, mobile apps, and satellite imagery for comprehensive monitoring
                        </p>
                        <div className="text-xs text-[#2D8B57] font-medium">782 active monitoring sites</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Analytics Platform</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Advanced data processing and predictive analytics for insights generation
                        </p>
                        <div className="text-xs text-[#20B2AA] font-medium">138,250 data points processed</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Reporting System</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Automated report generation and stakeholder dashboard interfaces
                        </p>
                        <div className="text-xs text-[#FF6B35] font-medium">782 evaluation reports generated</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Alert Mechanisms</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Real-time alerts and automated response triggers for rapid intervention
                        </p>
                        <div className="text-xs text-[#32CD32] font-medium">24-hour average response time</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Success Stories */}
                <div className="bg-gradient-to-r from-[#2D8B57]/10 to-[#20B2AA]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Success Stories</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20data%20scientist%20portrait%2C%20professional%20researcher%20with%20confident%20expression%2C%20monitoring%20equipment%20background%2C%20scientific%20expertise%2C%20natural%20lighting%2C%20technology%20integration&width=60&height=60&seq=testimonial-5&orientation=squarish"
                          alt="Dr. Andi"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Dr. Andi Wijaya</div>
                          <div className="text-sm text-gray-600">M&E Director, Jakarta</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "Our monitoring system caught a 20% decline in mangrove survival at Site 47 within 48 hours. Rapid intervention saved the entire restoration area. That's the power of real-time data."
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20field%20coordinator%20portrait%2C%20experienced%20monitoring%20specialist%20with%20professional%20demeanor%2C%20coastal%20field%20station%20background%2C%20scientific%20field%20work%2C%20natural%20lighting&width=60&height=60&seq=testimonial-6&orientation=squarish"
                          alt="Pak Joko"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Pak Joko Susanto</div>
                          <div className="text-sm text-gray-600">Field Coordinator, Demak</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "The mobile monitoring app transformed our field work. GPS tracking, photo verification, and instant data upload ensure every measurement is accurate and accountable."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Progress Tracking & Target Achievement
                  </h2>
                  <p className="text-lg text-gray-600">
                    Four-year evolution of monitoring and evaluation capabilities
                  </p>
                </div>

                {/* Overall Progress Chart */}
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-12">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Annual Progress Overview</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {progressData.map((data, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-[#2D8B57] mb-2">{data.year}</div>
                        <div className="text-lg font-medium text-[#333333] mb-2">{data.percentage}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div 
                            className="bg-gradient-to-r from-[#2D8B57] to-[#20B2AA] h-3 rounded-full transition-all duration-1000"
                            style={{ width: `${data.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {data.realisasi}/{data.target} evaluations
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#F8F9FA] rounded-lg p-6">
                    <h4 className="font-bold text-[#333333] mb-4 text-center">Cumulative Achievement</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#2D8B57] mb-1">782</div>
                        <div className="text-sm text-gray-600">Total Evaluations</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#20B2AA] mb-1">91.5%</div>
                        <div className="text-sm text-gray-600">Overall Achievement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#32CD32] mb-1">138,250</div>
                        <div className="text-sm text-gray-600">Data Points Collected</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Annual Breakdown */}
                <div className="space-y-8">
                  {progressData.map((data, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1">
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-[#2D8B57] to-[#20B2AA] rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                              {data.year}
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-[#333333]">{data.percentage}%</div>
                              <div className="text-gray-600">Achievement Rate</div>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Target:</span>
                              <span className="font-bold text-[#333333]">{data.target} evaluations</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Completed:</span>
                              <span className="font-bold text-[#2D8B57]">{data.realisasi} evaluations</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-[#2D8B57] h-2 rounded-full"
                                style={{ width: `${data.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Key Activities</h4>
                              <ul className="space-y-2">
                                {data.activities.map((activity, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-check-line text-[#2D8B57] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{activity}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Challenges</h4>
                              <ul className="space-y-2">
                                {data.challenges.map((challenge, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-alert-line text-[#FF6B6B] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{challenge}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Achievements</h4>
                              <ul className="space-y-2">
                                {data.achievements.map((achievement, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-trophy-line text-[#FFD93D] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Impact Tab */}
        {activeTab === 'impact' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Impact Assessment & Outcomes
                  </h2>
                  <p className="text-lg text-gray-600">
                    Measuring system performance and program effectiveness through data
                  </p>
                </div>

                {/* Impact Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center hover:shadow-lg transition-shadow duration-300">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* System Performance Assessment */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">System Performance</h3>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Data Accuracy</span>
                          <span className="font-bold text-[#2D8B57]">97%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#2D8B57] h-2 rounded-full" style={{ width: '97%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Verified accuracy of collected data points</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Response Time</span>
                          <span className="font-bold text-[#20B2AA]">89%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#20B2AA] h-2 rounded-full" style={{ width: '89%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Alerts responded to within 24 hours</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">System Uptime</span>
                          <span className="font-bold text-[#32CD32]">94%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#32CD32] h-2 rounded-full" style={{ width: '94%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">System availability and reliability</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">User Satisfaction</span>
                          <span className="font-bold text-[#FF6B35]">91%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#FF6B35] h-2 rounded-full" style={{ width: '91%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Stakeholder satisfaction with monitoring system</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Operational Efficiency</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-map-pin-line text-[#2D8B57]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Active Sites</div>
                            <div className="text-2xl font-bold text-[#2D8B57]">782</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Monitoring locations across Indonesia providing real-time data
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-time-line text-[#20B2AA]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Response Time</div>
                            <div className="text-2xl font-bold text-[#20B2AA]">24hrs</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Average time to respond to system alerts and anomalies
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-file-chart-line text-[#FF6B35]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Reports Generated</div>
                            <div className="text-2xl font-bold text-[#FF6B35]">782</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Comprehensive evaluation reports produced for stakeholders
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Long-term Impact */}
                <div className="bg-gradient-to-r from-[#2D8B57]/10 to-[#20B2AA]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Long-term Impact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-shield-check-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Program Accountability</h4>
                      <p className="text-gray-600 text-sm">
                        94% compliance rate ensures transparent and accountable program implementation across all sites
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-refresh-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Adaptive Management</h4>
                      <p className="text-gray-600 text-sm">
                        Real-time data enables rapid response and continuous improvement of conservation strategies
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#32CD32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-database-line text-2xl text-[#32CD32]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Evidence-Based Decisions</h4>
                      <p className="text-gray-600 text-sm">
                        Comprehensive data collection supports informed decision-making and strategic planning
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Methodology Tab */}
        {activeTab === 'methodology' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Monitoring & Evaluation Methodology
                  </h2>
                  <p className="text-lg text-gray-600">
                    Systematic approach to data collection, analysis, and program improvement
                  </p>
                </div>

                {/* Methodology Framework */}
                <div className="space-y-8 mb-12">
                  {[
                    {
                      step: 1,
                      title: 'Data Collection Framework',
                      description: 'Multi-source data collection using technology and community networks',
                      icon: 'ri-database-line',
                      color: '#2D8B57',
                      activities: [
                        'IoT sensor networks for real-time environmental monitoring',
                        'Mobile applications for field data collection and verification',
                        'Satellite imagery analysis for large-scale change detection',
                        'Community observer networks for ground-truth validation'
                      ]
                    },
                    {
                      step: 2,
                      title: 'Quality Assurance Protocols',
                      description: 'Rigorous data validation and verification procedures',
                      icon: 'ri-shield-check-line',
                      color: '#20B2AA',
                      activities: [
                        'GPS-tagged and time-stamped data collection',
                        'Photo verification for all field measurements',
                        'Cross-validation between multiple data sources',
                        'Regular calibration of monitoring equipment'
                      ]
                    },
                    {
                      step: 3,
                      title: 'Analytics & Processing',
                      description: 'Advanced data analysis and insight generation systems',
                      icon: 'ri-line-chart-line',
                      color: '#FF6B35',
                      activities: [
                        'Real-time data processing and anomaly detection',
                        'Predictive modeling for early warning systems',
                        'Statistical analysis and trend identification',
                        'Machine learning algorithms for pattern recognition'
                      ]
                    },
                    {
                      step: 4,
                      title: 'Reporting & Communication',
                      description: 'Stakeholder engagement through transparent reporting',
                      icon: 'ri-file-chart-line',
                      color: '#32CD32',
                      activities: [
                        'Automated report generation and distribution',
                        'Interactive dashboards for real-time monitoring',
                        'Stakeholder-specific communication formats',
                        'Public data sharing and transparency initiatives'
                      ]
                    }
                  ].map((step, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="flex items-start space-x-6">
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
                          style={{ backgroundColor: step.color }}
                        >
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center mb-4">
                            <i className={`${step.icon} text-2xl mr-3`} style={{ color: step.color }}></i>
                            <h3 className="text-2xl font-bold text-[#333333]">{step.title}</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-lg mb-6">
                            {step.description}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {step.activities.map((activity, idx) => (
                              <div key={idx} className="flex items-start space-x-3">
                                <div 
                                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                  style={{ backgroundColor: `${step.color}20` }}
                                >
                                  <i className="ri-check-line text-xs" style={{ color: step.color }}></i>
                                </div>
                                <span className="text-gray-700 text-sm">{activity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Evaluation Standards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Data Collection Methods</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center">
                          <i className="ri-smartphone-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Mobile Data Collection</h4>
                          <p className="text-sm text-gray-600">GPS-tagged field measurements with photo verification</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center">
                          <i className="ri-satellite-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Remote Sensing</h4>
                          <p className="text-sm text-gray-600">Satellite imagery and drone-based monitoring</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                          <i className="ri-sensor-line text-[#FF6B35]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">IoT Sensors</h4>
                          <p className="text-sm text-gray-600">Automated environmental monitoring systems</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#32CD32]/10 rounded-full flex items-center justify-center">
                          <i className="ri-team-line text-[#32CD32]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Community Networks</h4>
                          <p className="text-sm text-gray-600">Local observer networks for ground validation</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Evaluation Criteria</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-target-line text-[#2D8B57]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Relevance</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Program alignment with objectives and stakeholder needs
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-speed-line text-[#20B2AA]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Efficiency</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Resource utilization and cost-effectiveness analysis
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-checkbox-circle-line text-[#FF6B35]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Effectiveness</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Achievement of intended outcomes and impact goals
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Technology Integration */}
                <div className="bg-[#F8F9FA] rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Technology Integration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-cloud-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Cloud Platform</h4>
                      <p className="text-gray-600 text-sm mb-2">Scalable data storage</p>
                      <div className="text-xs text-gray-500">99.9% uptime</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-smartphone-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Mobile Apps</h4>
                      <p className="text-gray-600 text-sm mb-2">Field data collection</p>
                      <div className="text-xs text-gray-500">Real-time sync</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-brain-line text-2xl text-[#FF6B35]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">AI Analytics</h4>
                      <p className="text-gray-600 text-sm mb-2">Pattern recognition</p>
                      <div className="text-xs text-gray-500">Predictive insights</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#32CD32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-dashboard-line text-2xl text-[#32CD32]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Dashboards</h4>
                      <p className="text-gray-600 text-sm mb-2">Real-time visualization</p>
                      <div className="text-xs text-gray-500">Interactive reports</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="py-16 bg-[#2D8B57] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Experience Data-Driven Conservation
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Discover how advanced monitoring and evaluation systems ensure transparent and effective conservation programs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#2D8B57] whitespace-nowrap"
                  href="/program"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Program
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#2D8B57] whitespace-nowrap"
                  href="/tentang"
                >
                  <i className="ri-information-line mr-2"></i>
                  Learn More About M4CR
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
